import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import AdvertisementModal from './AdvertisementModal'
import { detectUserCountry } from '../utils/detectUserCountry'

const ONETIME_BANNER_SHOWN_KEY = 'promo_banner_onetime_shown'
const ACTIVE_BANNER_STATE_KEY = 'promo_banner_active_state'
const IMPRESSION_STATE_KEY = 'promo_banner_impression_state'

const isOnetimeBanner = (banner) => (
  banner?.displayPlatform === 'onetime' ||
  banner?.display_platform === 'onetime'
)

const getOnetimeBannerShown = () => {
  try {
    return localStorage.getItem(ONETIME_BANNER_SHOWN_KEY) === '1'
  } catch {
    return false
  }
}

const setOnetimeBannerShown = () => {
  try {
    localStorage.setItem(ONETIME_BANNER_SHOWN_KEY, '1')
  } catch {}
}

const getActiveBannerState = (placement) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(ACTIVE_BANNER_STATE_KEY) || '{}'
    )

    const placementState = state?.[placement]

    return {
      bannerId: Number.isFinite(Number(placementState?.bannerId))
        ? Number(placementState.bannerId)
        : null,

      expiresAt: Number.isFinite(Number(placementState?.expiresAt))
        ? Number(placementState.expiresAt)
        : 0,

      videoTime: Number.isFinite(Number(placementState?.videoTime))
        ? Number(placementState.videoTime)
        : 0,

      lang: normalizeLanguage(placementState?.lang),

      country: normalizeCountry(placementState?.country)
    }
  } catch {
    return {
      bannerId: null,
      expiresAt: 0,
      videoTime: 0,
      lang: null,
      country: null    }
  }
}

const setActiveBannerState = (
  placement,
  bannerId,
  expiresAt,
  videoTime,
  lang,
  country
) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(ACTIVE_BANNER_STATE_KEY) || '{}'
    )

    localStorage.setItem(
      ACTIVE_BANNER_STATE_KEY,
      JSON.stringify({
        ...state,
        [placement]: {
          bannerId,
          expiresAt,
          videoTime,
          lang: normalizeLanguage(lang),
          country: normalizeCountry(country)
        }
      })
    )
  } catch {}
}

const clearActiveBannerState = (placement) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(ACTIVE_BANNER_STATE_KEY) || '{}'
    )

    delete state[placement]

    localStorage.setItem(
      ACTIVE_BANNER_STATE_KEY,
      JSON.stringify(state)
    )
  } catch {}
}

const hasBannerImpression = (placement, bannerId) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(IMPRESSION_STATE_KEY) || '{}'
    )

    return (
      Number(state?.[placement]?.bannerId) ===
      Number(bannerId)
    )
  } catch {
    return false
  }
}

const setBannerImpression = (
  placement,
  bannerId
) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(IMPRESSION_STATE_KEY) || '{}'
    )

    localStorage.setItem(
      IMPRESSION_STATE_KEY,
      JSON.stringify({
        ...state,
        [placement]: {
          bannerId
        }
      })
    )
  } catch {}
}

const clearBannerImpression = (
  placement
) => {
  try {
    const state = JSON.parse(
      localStorage.getItem(IMPRESSION_STATE_KEY) || '{}'
    )

    delete state[placement]

    localStorage.setItem(
      IMPRESSION_STATE_KEY,
      JSON.stringify(state)
    )
  } catch {}
}

const getRotateMs = (banner) => {
  const seconds = Number(
    banner?.rotate_seconds
  )

  return Number.isFinite(seconds) &&
    seconds > 0
    ? seconds * 1000
    : 15000
}

const DISPLAY_PLATFORM = 'web'

const NEXT_BANNER_PRELOAD_BEFORE_MS = 1000

const normalizeLanguage = (value) => {
  if (typeof value !== 'string') return null

  const normalized = value.split('-')[0].trim().toLowerCase()

  return normalized && normalized !== 'null' ? normalized : null
}

const normalizeCountry = (value) => {
  const raw = typeof value === 'string'
    ? value
    : value?.country

  if (typeof raw !== 'string') return null

  const normalized = raw.trim().toUpperCase()

  return normalized && normalized !== 'NULL' ? normalized : null
}

const getBannerCountryCode = (banner) => {
  return normalizeCountry(
    banner?.country_code ||
    banner?.countryCode
  )
}

const isBannerVisibleForCountry = (banner, country) => {
  const bannerCountry = getBannerCountryCode(banner)

  return !bannerCountry || !country || bannerCountry === country
}

const getBannerLanguage = (banner) => {
  return normalizeLanguage(
    banner?.lang ||
    banner?.language
  )
}

const isBannerVisibleForLanguage = (banner, lang) => {
  const bannerLang = getBannerLanguage(banner)

  return !bannerLang || !lang || bannerLang === lang
}

const PromoBannerModal = ({
  fallback,
  placement = 'home',
  rememberFirstBanner = true,
  autoRotate = true,
  restartActiveBanner = false,
  onItemsChange,
  showFullScreenButton = true
}) => {
  const { i18n } = useTranslation()
  const [items, setItems] = useState([])
  const [muted, setMuted] = useState(true)
  const [videoHasAudio, setVideoHasAudio] = useState(false)
  const [openAdvertisement, setOpenAdvertisement] = useState(false)
  const [fullScreen, setFullScreen] = useState(false)
const [nextVideoUrl, setNextVideoUrl] = useState(null)
const [videoRotationKey, setVideoRotationKey] = useState(0)

const bannerRef = useRef(null)
const videoRef = useRef(null)
const preparedNextBannersRef = useRef(null)
const preloadedNextVideoUrlRef = useRef(null)
const preloadRequestRef = useRef(0)
const videoSaveIntervalRef = useRef(null)

  const commitBannerItems = useCallback((
    filtered,
    country,
    lang
  ) => {
    const activeBanner = filtered[0]

    if (activeBanner?.id) {
      const existingState =
        getActiveBannerState(placement)

      const sameLocale =
        existingState.lang === lang &&
        existingState.country === country

      const expiresAt =
        !restartActiveBanner &&
        sameLocale &&
        Number(existingState.bannerId) ===
          Number(activeBanner.id) &&
        existingState.expiresAt > Date.now()
          ? existingState.expiresAt
          : Date.now() +
            getRotateMs(activeBanner)

      setActiveBannerState(
        placement,
        activeBanner.id,
        expiresAt,
        !restartActiveBanner && sameLocale
          ? (existingState.videoTime || 0)
          : 0,
        lang,
        country
      )
    } else {
      clearActiveBannerState(placement)
    }

    setItems(filtered)
  }, [
    placement,
    restartActiveBanner
  ])

useEffect(() => {
  if (!fullScreen) return undefined

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setFullScreen(false)
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return () => {
    window.removeEventListener('keydown', handleKeyDown)
  }
}, [fullScreen])

  const loadBanners = useCallback(async ({
    commit = true,
    forceNext = false
  } = {}) => {
    const detectedCountry = await detectUserCountry()
    const country = normalizeCountry(detectedCountry)
    const lang = normalizeLanguage(i18n.language)

    const excludeOnetime = getOnetimeBannerShown()
    const excludeOnetimeParam = excludeOnetime
      ? '&excludeOnetime=1'
      : ''

    const activeBannerState =
      getActiveBannerState(placement)

    const canContinueActiveBanner =
      !forceNext &&
      !restartActiveBanner &&
      activeBannerState.bannerId &&
      activeBannerState.expiresAt > Date.now() &&
      activeBannerState.lang === lang &&
      activeBannerState.country === country

    const currentBannerParam = canContinueActiveBanner
      ? `&currentBannerId=${encodeURIComponent(
        activeBannerState.bannerId
      )}`
      : ''

    const countryParam = country || 'null'
    const langParam = lang || 'null'

    const res = await fetchWithAuth(
      `/promo-banners?placement=${encodeURIComponent(placement)}&country=${encodeURIComponent(countryParam)}&platform=${DISPLAY_PLATFORM}&lang=${encodeURIComponent(langParam)}${excludeOnetimeParam}${currentBannerParam}`,
      { cacheTtlMs: 0 },
      langParam
    )

if (!res.ok) {
  if (!commit) {
    preparedNextBannersRef.current = null
    preloadedNextVideoUrlRef.current = null
    setNextVideoUrl(null)
  } else {
    setItems([])
  }

  return []
}

    const data = await res.json()

    let filtered = Array.isArray(data?.banners)
      ? data.banners.filter(
        x => x.active !== false &&
          isBannerVisibleForCountry(x, country) &&
          isBannerVisibleForLanguage(x, lang)
      )
      : []

    if (getOnetimeBannerShown()) {
      filtered = filtered.filter(
        x => !isOnetimeBanner(x)
      )
    }

    const activeBanner = filtered[0]

if (!commit) {
  const requestId = preloadRequestRef.current + 1
  preloadRequestRef.current = requestId

  const nextVideoUrlValue =
    activeBanner?.type === 'video' &&
    activeBanner.videoUrl
      ? activeBanner.videoUrl
      : null

  if (preloadRequestRef.current !== requestId) {
    return filtered
  }

  preparedNextBannersRef.current = {
    items: filtered,
    country,
    lang
  }

  preloadedNextVideoUrlRef.current =
    nextVideoUrlValue

  setNextVideoUrl(nextVideoUrlValue)

  return filtered
}

commitBannerItems(
  filtered,
  country,
  lang
)

preparedNextBannersRef.current = null
preloadedNextVideoUrlRef.current = null
setNextVideoUrl(null)

    return filtered
  }, [
    i18n.language,
    placement,
    restartActiveBanner,
    commitBannerItems
  ])

  const removeBanner = (bannerId) => {
    setItems(prev =>
      prev.filter(
        x => Number(x.id) !== Number(bannerId)
      )
    )
  }

  useEffect(() => {
    loadBanners()
  }, [loadBanners])

  useEffect(() => {
    const currentBanner = items[0]
    const bannerElement = bannerRef.current

    if (!currentBanner?.id || !bannerElement) return undefined

    let impressionSent = false
    let bannerVisible = false

    const isPageActive = () => {
      if (typeof document === 'undefined') return true

      return document.visibilityState === 'visible'
    }

    const updateBannerVisible = () => {
      const rect = bannerElement.getBoundingClientRect()
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight
      const visibleWidth =
        Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0)
      const visibleHeight =
        Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
      const visibleArea = Math.max(0, visibleWidth) * Math.max(0, visibleHeight)
      const bannerArea = rect.width * rect.height

      bannerVisible = bannerArea > 0 && visibleArea / bannerArea >= 0.5
    }

const sendImpression = async () => {
  if (impressionSent || !isPageActive() || !bannerVisible) return

  if (
    hasBannerImpression(
      placement,
      currentBanner.id
    )
  ) {
    impressionSent = true
    return
  }

  impressionSent = true

  if (rememberFirstBanner && isOnetimeBanner(currentBanner)) {
    setOnetimeBannerShown()
  }

  try {
        const res = await fetchWithAuth(
          '/promo-banners/impression',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              bannerId: currentBanner.id
            })
          }
        )

        if (!res.ok) return

setBannerImpression(
  placement,
  currentBanner.id
)

        const data = await res.json()

        if (data?.exhausted) {
          clearActiveBannerState(placement)
	clearBannerImpression(placement)
	removeBanner(currentBanner.id)
        }
      } catch {}
    }

    const checkImpression = () => {
      updateBannerVisible()
      sendImpression()
    }

    let observer

    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        entries => {
          const entry = entries[0]

          bannerVisible =
            !!entry?.isIntersecting && entry.intersectionRatio >= 0.5
          sendImpression()
        },
        { threshold: [0.5] }
      )
      observer.observe(bannerElement)
    }

    checkImpression()

    document.addEventListener('visibilitychange', checkImpression)
    window.addEventListener('resize', checkImpression)
    window.addEventListener('scroll', checkImpression, true)

    return () => {
      observer?.disconnect()
      document.removeEventListener('visibilitychange', checkImpression)
      window.removeEventListener('resize', checkImpression)
      window.removeEventListener('scroll', checkImpression, true)
    }
  }, [items, placement, rememberFirstBanner])

useEffect(() => {
  if (!autoRotate) return undefined

  const currentBanner = items[0]
  let cancelled = false
  let clearNextVideoTimeout

  const activeBannerState =
    getActiveBannerState(placement)

  const delay =
    currentBanner?.id &&
    Number(activeBannerState.bannerId) ===
      Number(currentBanner.id) &&
    activeBannerState.expiresAt > Date.now()
      ? activeBannerState.expiresAt -
        Date.now()
      : getRotateMs(currentBanner)

  const loadNextBanners = async () => {
    const filtered = await loadBanners({
      commit: false,
      forceNext: true
    })

    if (cancelled) {
      return null
    }

    return Array.isArray(filtered)
      ? filtered
      : []
  }

  const preloadTimeout = setTimeout(
    loadNextBanners,
    Math.max(
      0,
      delay - NEXT_BANNER_PRELOAD_BEFORE_MS
    )
  )

  const rotate = async () => {
    const preparedNextBanners =
      preparedNextBannersRef.current

    let filtered =
      preparedNextBanners?.items

    if (!Array.isArray(filtered)) {
      filtered = await loadNextBanners()
    }

    if (
      cancelled ||
      !Array.isArray(filtered)
    ) {
      return
    }

    const nextBanner = filtered[0]

    clearActiveBannerState(placement)
    clearBannerImpression(placement)

    commitBannerItems(
      filtered,
      preparedNextBanners?.country ||
        normalizeCountry(
          await detectUserCountry()
        ),
      preparedNextBanners?.lang ||
        normalizeLanguage(i18n.language)
    )

    if (
      nextBanner?.type === 'video' &&
      nextBanner.videoUrl &&
      nextBanner.videoUrl ===
        preloadedNextVideoUrlRef.current
    ) {
      setVideoRotationKey(
        key => key + 1
      )
    }

    preparedNextBannersRef.current = null
    preloadedNextVideoUrlRef.current = null

    clearNextVideoTimeout =
      window.setTimeout(() => {
        setNextVideoUrl(null)
      }, 0)
  }

  const timeout = setTimeout(
    rotate,
    Math.max(0, delay)
  )

  return () => {
    cancelled = true
    clearTimeout(preloadTimeout)
    clearTimeout(timeout)
    clearTimeout(clearNextVideoTimeout)
  }
}, [
  items,
  placement,
  loadBanners,
  autoRotate,
  commitBannerItems,
  i18n.language
])

  useEffect(() => {
    if (typeof onItemsChange === 'function') {
      onItemsChange(items)
    }
  }, [items, onItemsChange])

  const currentVideoIdentity = items[0]
    ? `${items[0].id}:${items[0].videoUrl || ''}`
    : ''

  useEffect(() => {
    setVideoHasAudio(false)
  }, [currentVideoIdentity])

useEffect(() => {
  const banner = items[0]
  const video = videoRef.current

  if (
    !banner?.id ||
    banner.type !== 'video' ||
    !video
  ) {
    return undefined
  }

    const interval = setInterval(() => {
      const state =
        getActiveBannerState(placement)

      if (
        Number(state.bannerId) !==
        Number(banner.id)
      ) {
        return
      }

      setActiveBannerState(
        placement,
        banner.id,
        state.expiresAt,
        video.currentTime,
        state.lang,
        state.country
      )
    }, 1000)

    videoSaveIntervalRef.current =
      interval

    return () => {
      clearInterval(interval)
    }
  }, [items, placement])

useEffect(() => {
  const video = videoRef.current

  const currentBanner = items[0]

  if (
    !video ||
    currentBanner?.type !== 'video'
  ) {
    return undefined
  }

  const state =
    getActiveBannerState(placement)

    const restoreVideoPosition = () => {
      if (
        Number(state.bannerId) !==
        Number(currentBanner?.id)
      ) {
        return
      }

      if (
        !restartActiveBanner &&
        Number.isFinite(state.videoTime) &&
        state.videoTime > 0 &&
        state.videoTime < video.duration
      ) {
        video.currentTime =
          state.videoTime
      }
    }

    restoreVideoPosition()

    video.addEventListener(
      'loadedmetadata',
      restoreVideoPosition
    )

    const detectAudio = () => {
      const audioTracks = video.audioTracks
      const hasAudioTrack =
        audioTracks && typeof audioTracks.length === 'number'
          ? audioTracks.length > 0
          : false
      const hasWebkitAudio =
        typeof video.webkitAudioDecodedByteCount === 'number' &&
        video.webkitAudioDecodedByteCount > 0
      const hasMozAudio = video.mozHasAudio === true

      setVideoHasAudio(
        hasAudioTrack || hasWebkitAudio || hasMozAudio
      )
    }

    video.muted = muted
    detectAudio()

    video.addEventListener('loadedmetadata', detectAudio)
    video.addEventListener('loadeddata', detectAudio)
    video.addEventListener('canplay', detectAudio)
    video.addEventListener('timeupdate', detectAudio)

    return () => {
      video.removeEventListener(
        'loadedmetadata',
        restoreVideoPosition
      )

      video.removeEventListener('loadedmetadata', detectAudio)
      video.removeEventListener('loadeddata', detectAudio)
      video.removeEventListener('canplay', detectAudio)
      video.removeEventListener('timeupdate', detectAudio)
    }
  }, [muted, items, placement, restartActiveBanner])

  if (!items.length) {
    return fallback
  }

const banner = items[0]

const bannerWindow = (
  <div
    style={
      fullScreen
        ? styles.fullScreenContainer
        : styles.container
    }
  >
    <div
      ref={bannerRef}
      style={{
        ...styles.bannerLayer,
        display: 'flex',
        cursor:
          banner.targetUrl ||
          banner.actionType === 'modal'
            ? 'pointer'
            : 'default',
      }}
      onClick={async (e) => {
        e.stopPropagation()

        try {
          await fetchWithAuth(
            '/promo-banners/click',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                bannerId: banner.id
              })
            }
          )
        } catch {}

	if (banner.actionType === 'modal') {
 	setFullScreen(false)
 	 setOpenAdvertisement(true)
 	 return
	}

        if (!banner.targetUrl) {
          return
        }

        window.open(
          banner.targetUrl,
          '_blank',
          'noopener,noreferrer'
        )
      }}
    >
      {banner.type === 'image' && banner.imageUrl && (
        <img
          src={banner.imageUrl}
          alt={banner.title || ''}
          style={styles.image}
        />
      )}

<VideoBanner
  uri={
    banner.type === 'video' &&
    banner.videoUrl
      ? banner.videoUrl
      : null
  }
  nextUri={nextVideoUrl}
  rotationKey={videoRotationKey}
  visible={
    banner.type === 'video' &&
    !!banner.videoUrl
  }
  muted={muted}
  videoRef={videoRef}
  onHasAudio={setVideoHasAudio}
  soundControl={
    banner.type === 'video' &&
    videoHasAudio && (
      <button
        type="button"
        style={styles.soundButton}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setMuted(value => !value)
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
    )
  }
/>

      {banner.type === 'text' && (
        <div style={styles.textContainer}>
          <div style={styles.title}>
            {banner.title}
          </div>

          {!!banner.description && (
            <div style={styles.description}>
              {banner.description.replace(/\\n/g, '\n')}
            </div>
          )}
        </div>
      )}
    </div>

{showFullScreenButton && (
  <button
    type="button"
    aria-label={
      fullScreen
        ? 'Zmenšiť banner'
        : 'Zväčšiť banner'
    }
    title={
      fullScreen
        ? 'Zmenšiť banner'
        : 'Zväčšiť banner'
    }
    style={styles.fullScreenButton}
    onClick={(e) => {
      e.preventDefault()
      e.stopPropagation()
      setFullScreen(value => !value)
    }}
  >
    {fullScreen ? '×' : '⛶'}
  </button>
)}
  </div>
)

return (
  <>
    {fullScreen
      ? createPortal(
<div
  style={styles.fullScreenBackdrop}
  onClick={() => setFullScreen(false)}
>
  <div onClick={(event) => event.stopPropagation()}>
    {bannerWindow}
  </div>
</div>,
        document.body
      )
      : bannerWindow}

    <AdvertisementModal
      open={openAdvertisement}
      onClose={() => setOpenAdvertisement(false)}
    />
  </>
)
}

const styles = {
  container: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 10
  },

fullScreenBackdrop: {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.72)',
  zIndex: 2147483646
},

fullScreenContainer: {
  position: 'relative',
  width: 'min(90vw, 1200px)',
  height: 'min(46.4vw, 620px)',
  maxHeight: '90vh',
  overflow: 'hidden',
  borderRadius: 10,
  boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
  background: '#000'
},

fullScreenButton: {
  position: 'absolute',
  top: 8,
  right: 8,
  width: 30,
  height: 30,
  padding: 0,
  border: 0,
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.62)',
  color: '#fff',
  fontSize: 18,
  lineHeight: '30px',
  textAlign: 'center',
  cursor: 'pointer',
  zIndex: 2147483647
},

  bannerLayer: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },

  videoWrap: {
    width: '100%',
    height: '100%',
    position: 'relative'
  },

  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },

visibleVideo: {
  opacity: 1
},

hiddenVideo: {
  opacity: 0,
  position: 'absolute',
  inset: 0
},

  textContainer: {
    width: '100%',
    height: '100%',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },

  title: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 16
  },

  description: {
    textAlign: 'center',
    marginTop: 4,
    fontSize: 14,
    whiteSpace: 'pre-line'
  },

  soundButton: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    backgroundColor: 'rgba(0,0,0,0.6)',
    padding: '6px 10px',
    borderRadius: 20,
    zIndex: 100,
    border: 'none',
    color: '#fff',
    fontSize: 16,
    cursor: 'pointer'
  }
}

const configureVideo = (
  video,
  muted
) => {
  if (!video) return

  video.loop = true
  video.muted = muted
  video.playsInline = true
  video.preload = 'auto'
}

const playVideo = (video) => {
  if (!video) return

  const playPromise = video.play()

  if (
    playPromise &&
    typeof playPromise.catch === 'function'
  ) {
    playPromise.catch(() => {})
  }
}

const detectVideoAudio = (video) => {
  if (!video) return false

  const audioTracks = video.audioTracks

  const hasAudioTrack =
    audioTracks &&
    typeof audioTracks.length === 'number'
      ? audioTracks.length > 0
      : false

  const hasWebkitAudio =
    typeof video.webkitAudioDecodedByteCount ===
      'number' &&
    video.webkitAudioDecodedByteCount > 0

  const hasMozAudio =
    video.mozHasAudio === true

  return (
    hasAudioTrack ||
    hasWebkitAudio ||
    hasMozAudio
  )
}

const VideoBanner = ({
  uri,
  nextUri,
  rotationKey,
  visible,
  muted,
  videoRef,
  onHasAudio,
  soundControl
}) => {
  const [activeSlot, setActiveSlot] =
    useState('primary')

  const primaryRef = useRef(null)
  const preloadRef = useRef(null)
  const wasVisibleRef = useRef(false)
  const activeUriRef = useRef(null)
  const preloadedUriRef = useRef(null)
  const lastRotationKeyRef =
    useRef(rotationKey)

  const getActiveVideo = useCallback(
    () => (
      activeSlot === 'primary'
        ? primaryRef.current
        : preloadRef.current
    ),
    [activeSlot]
  )

  const getInactiveVideo = useCallback(
    () => (
      activeSlot === 'primary'
        ? preloadRef.current
        : primaryRef.current
    ),
    [activeSlot]
  )

  useEffect(() => {
    configureVideo(
      primaryRef.current,
      muted
    )

    configureVideo(
      preloadRef.current,
      muted
    )
  }, [muted])

  useEffect(() => {
    const activeVideo =
      getActiveVideo()

    videoRef.current = activeVideo

    return () => {
      if (videoRef.current === activeVideo) {
        videoRef.current = null
      }
    }
  }, [
    activeSlot,
    getActiveVideo,
    videoRef
  ])

  useEffect(() => {
    const inactiveVideo =
      getInactiveVideo()

    if (
      !nextUri ||
      nextUri === activeUriRef.current ||
      !inactiveVideo
    ) {
      return undefined
    }

    preloadedUriRef.current = null

    inactiveVideo.src = nextUri
    inactiveVideo.currentTime = 0
    inactiveVideo.muted = true
    inactiveVideo.load()

    const handleReady = () => {
      inactiveVideo.currentTime = 0
      inactiveVideo.muted = true
      playVideo(inactiveVideo)

      preloadedUriRef.current =
        nextUri
    }

    const handleError = () => {
      preloadedUriRef.current = null
    }

    inactiveVideo.addEventListener(
      'loadeddata',
      handleReady
    )

    inactiveVideo.addEventListener(
      'canplay',
      handleReady
    )

    inactiveVideo.addEventListener(
      'error',
      handleError
    )

    return () => {
      inactiveVideo.removeEventListener(
        'loadeddata',
        handleReady
      )

      inactiveVideo.removeEventListener(
        'canplay',
        handleReady
      )

      inactiveVideo.removeEventListener(
        'error',
        handleError
      )
    }
  }, [
    nextUri,
    getInactiveVideo
  ])

  useEffect(() => {
    const activeVideo =
      getActiveVideo()

    const inactiveVideo =
      getInactiveVideo()

    const rotationChanged =
      lastRotationKeyRef.current !==
      rotationKey

    if (rotationChanged) {
      lastRotationKeyRef.current =
        rotationKey

      if (
        preloadedUriRef.current === uri &&
        inactiveVideo
      ) {
        try {
          activeVideo?.pause()
          inactiveVideo.muted = muted

          if (visible) {
            playVideo(inactiveVideo)
          }
        } catch {}

        setActiveSlot(slot =>
          slot === 'primary'
            ? 'preload'
            : 'primary'
        )

        activeUriRef.current = uri
        preloadedUriRef.current = null

        return
      }
    }

    if (
      uri &&
      activeUriRef.current !== uri &&
      activeVideo
    ) {
      activeUriRef.current = uri
      activeVideo.src = uri
      activeVideo.currentTime = 0
      activeVideo.load()

      if (visible) {
        playVideo(activeVideo)
      }
    }
  }, [
    uri,
    rotationKey,
    visible,
    muted,
    getActiveVideo,
    getInactiveVideo
  ])

  useEffect(() => {
    const activeVideo =
      getActiveVideo()

    if (!activeVideo) {
      return undefined
    }

    const wasVisible =
      wasVisibleRef.current

    wasVisibleRef.current =
      visible

    if (visible) {
      try {
        if (!wasVisible) {
          activeVideo.currentTime = 0
        }

        activeVideo.muted = muted
        playVideo(activeVideo)
      } catch {}
    } else {
      try {
        activeVideo.pause()
      } catch {}
    }

    return undefined
  }, [
    visible,
    activeSlot,
    muted,
    getActiveVideo
  ])

  useEffect(() => {
    const activeVideo =
      getActiveVideo()

    if (!activeVideo || !visible) {
      onHasAudio(false)
      return undefined
    }

    const handleAudio = () => {
      onHasAudio(
        detectVideoAudio(activeVideo)
      )
    }

    handleAudio()

    activeVideo.addEventListener(
      'loadedmetadata',
      handleAudio
    )

    activeVideo.addEventListener(
      'loadeddata',
      handleAudio
    )

    activeVideo.addEventListener(
      'canplay',
      handleAudio
    )

    activeVideo.addEventListener(
      'timeupdate',
      handleAudio
    )

    return () => {
      activeVideo.removeEventListener(
        'loadedmetadata',
        handleAudio
      )

      activeVideo.removeEventListener(
        'loadeddata',
        handleAudio
      )

      activeVideo.removeEventListener(
        'canplay',
        handleAudio
      )

      activeVideo.removeEventListener(
        'timeupdate',
        handleAudio
      )
    }
  }, [
    visible,
    activeSlot,
    getActiveVideo,
    onHasAudio
  ])

  return (
    <div
      style={{
        ...styles.videoWrap,
        display: visible ? 'block' : 'none'
      }}
    >
      <video
        ref={primaryRef}
        style={{
          ...styles.video,
          ...(activeSlot === 'primary'
            ? styles.visibleVideo
            : styles.hiddenVideo)
        }}
        muted={muted}
        autoPlay
        loop
        playsInline
      />

      <video
        ref={preloadRef}
        style={{
          ...styles.video,
          ...(activeSlot === 'preload'
            ? styles.visibleVideo
            : styles.hiddenVideo)
        }}
        muted
        autoPlay
        loop
        playsInline
      />

      {soundControl}
    </div>
  )
}

export default PromoBannerModal